import { useState, useEffect } from 'react'
import { SimpleForm, TextInput, SaveButton, Toolbar } from 'react-admin'
import { Box, Typography, Grid, Autocomplete, Chip, Alert } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { styled } from '@mui/material/styles'
import { PartnerGeneralInfo } from '../types/PartnerGeneralInfo'
import { FieldValues } from 'react-hook-form'
import useAccount from '../hooks/useAccount'
import { AddressVerification } from './AddressVerification'
import { AddressData, getAddresses } from '../repository/getAddresses'
import useAlert from '../../../components/Alert/hooks/useAlert'

export type VendorAccountInfoProps = {
  partner: PartnerGeneralInfo
}

const styles = {
  helpIcon: {
    fontSize: '16px',
    color: 'grey.500',
    marginLeft: 0.5,
    verticalAlign: 'middle'
  },
  container: {
    padding: 2
  },
  typography: {
    marginTop: 2
  },
  formBox: {
    display: 'flex',
    alignItems: 'center'
  }
}

const HelpIcon = styled(HelpOutlineIcon)(() => styles.helpIcon)

const validateAddress = (data: AddressData) => {
  const errors = []
  if (!data.address_1) errors.push('Address is required')
  if (!data.city) errors.push('City is required')
  if (!data.zip) errors.push('Zip code is required')
  if (!data.country) errors.push('Country is required')

  return errors
}

const parseEmails = (emailString: string | null = ''): string[] => {
  if (emailString === null) {
    return []
  }
  return emailString
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean)
}

const formatEmails = (emails: string[]): string => {
  return emails.join(',')
}

export const VendorAccountInfo = ({ partner }: VendorAccountInfoProps) => {
  const { updateVendorInfo } = useAccount()
  const { alertErrors } = useAlert()
  const [formData, setFormData] = useState<PartnerGeneralInfo>(partner)
  const [parsedEmails, setParsedEmails] = useState({
    supportContactStackCommerce: parseEmails(
      partner.supportContactStackCommerce
    ),
    supportContactCustomers: parseEmails(partner.supportContactCustomers),
    fulfillmentContact: parseEmails(partner.fulfillmentContact)
  })

  const handleEmailChange =
    (field: keyof typeof parsedEmails) => (_: any, newValue: string[]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: formatEmails(newValue)
      }))
    }

  useEffect(() => {
    setParsedEmails({
      supportContactStackCommerce: parseEmails(
        formData.supportContactStackCommerce
      ),
      supportContactCustomers: parseEmails(formData.supportContactCustomers),
      fulfillmentContact: parseEmails(formData.fulfillmentContact)
    })
  }, [
    formData.supportContactStackCommerce,
    formData.supportContactCustomers,
    formData.fulfillmentContact
  ])

  const [modalVisible, setModalVisible] = useState(false)
  const toggleModal = () => setModalVisible(!modalVisible)

  const [providedAddress, setProvidedAddress] = useState<AddressData | null>(
    null
  )
  const [suggestedAddress, setSuggestedAddress] = useState<AddressData | null>(
    null
  )
  const [addressStatus, setAddressStatus] = useState<'invalid' | 'suggested'>()
  const [addressError, setAddressError] = useState<string[]>([])

  const handleAddress = async (values: FieldValues) => {
    const formAddress = {
      address_1: values.address1,
      address_2: values.address2,
      city: values.city,
      country: values.country,
      phone_number: values.fulfillmentPhoneNumber,
      state: values.state,
      zip: values.zipcode
    }

    const errors = validateAddress(formAddress)
    if (errors.length > 0) {
      alertErrors(errors)
      return
    }

    setProvidedAddress(formAddress)

    const response = await getAddresses(formAddress)

    if (response.kind === 'error') {
      alertErrors([response.message])
    } else {
      const suggestedAddress = response.data.attributes.suggested_address
      const addressStatus = response.data.attributes.status
      const addressErrors = response.data.attributes.errors.map(
        (error: { field: string; message: string }) => error.message
      )

      setSuggestedAddress(suggestedAddress)
      setAddressStatus(addressStatus)
      setAddressError(addressErrors)
      toggleModal()
    }
  }

  const submitInfoUpdate = (values: FieldValues) => {
    const mergedData = {
      primaryContact: values.primaryContact,
      phoneNumber: values.phoneNumber,
      fulfillmentContact: formData.fulfillmentContact,
      supportContactCustomers: formData.supportContactCustomers,
      supportContactStackCommerce: formData.supportContactStackCommerce,
      supportFormUrl: values.supportFormUrl
    }

    updateVendorInfo(mergedData)
  }

  const SaveOnlyToolbar = () => (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  )

  return (
    <Box sx={styles.container}>
      {partner.addressVerificationState === 'unverified' && (
        <Alert severity="info">
          We have implemented an address verification update, and need you to
          take a moment to verify your fulfillment address below. Thank you!
        </Alert>
      )}
      <SimpleForm
        data-testid="update-vendor-info-form"
        toolbar={<SaveOnlyToolbar />}
        record={partner}
        onSubmit={submitInfoUpdate}
      >
        <Typography variant="h6" gutterBottom sx={styles.typography}>
          General Information
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              source="primaryContact"
              label="Primary Contact"
              type="email"
              fullWidth
              isRequired
              inputProps={{ 'data-testid': 'primary-contact' }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              source="phoneNumber"
              label="Phone Number"
              fullWidth
              inputProps={{ 'data-testid': 'phone-number' }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={styles.formBox}>
              <Autocomplete
                clearIcon={false}
                options={[]}
                freeSolo
                multiple
                value={parsedEmails.supportContactStackCommerce}
                onChange={handleEmailChange('supportContactStackCommerce')}
                renderTags={(value, props) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...props({ index })}
                      key={`supportContactStackCommerce-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextInput
                    source="supportContactStackCommerce"
                    type="email"
                    {...params}
                    label="Support Contact (StackCommerce)"
                    helperText="Add emails"
                    data-testid="support-stack"
                  />
                )}
                fullWidth
              />
              <HelpIcon />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={styles.formBox}>
              <Autocomplete
                clearIcon={false}
                options={[]}
                freeSolo
                multiple
                value={parsedEmails.supportContactCustomers}
                onChange={handleEmailChange('supportContactCustomers')}
                renderTags={(value, props) =>
                  value.map((option, index) => (
                    <Chip
                      {...props({ index })}
                      label={option}
                      key={`supportContactCustomers-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextInput
                    source="supportContactCustomers"
                    {...params}
                    label="Support Contact (Customers)"
                    helperText="Add emails"
                    data-testid="support-customers"
                  />
                )}
                fullWidth
              />
              <HelpIcon />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={styles.formBox}>
              <TextInput
                source="supportFormUrl"
                label="Support Form URL"
                fullWidth
                inputProps={{ 'data-testid': 'support-url' }}
              />
              <HelpIcon />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              clearIcon={false}
              options={[]}
              freeSolo
              multiple
              value={parsedEmails.fulfillmentContact}
              onChange={handleEmailChange('fulfillmentContact')}
              renderTags={(value, props) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...props({ index })}
                    key={`fulfillmentContact-${index}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextInput
                  {...params}
                  source="fulfillmentContact"
                  label="Fulfillment Contact"
                  helperText="Add emails"
                  data-testid="support-fulfillment"
                />
              )}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              source="fulfillmentPhoneNumber"
              label="Fulfillment Phone Number"
              fullWidth
            />
          </Grid>
        </Grid>
      </SimpleForm>

      <SimpleForm
        data-testid="update-vendor-address-form"
        toolbar={<SaveOnlyToolbar />}
        onSubmit={handleAddress}
        record={partner}
      >
        <Typography variant="h6" gutterBottom sx={styles.typography}>
          Address Information
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              source="address1"
              label="Return Address"
              fullWidth
              inputProps={{ 'data-testid': 'address-1' }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              source="address2"
              label="Return Address Line 2"
              fullWidth
              inputProps={{ 'data-testid': 'address-2' }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              source="city"
              label="City"
              fullWidth
              inputProps={{ 'data-testid': 'city' }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              source="state"
              label="State"
              fullWidth
              inputProps={{ 'data-testid': 'state' }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              source="zipcode"
              label="Zipcode"
              fullWidth
              inputProps={{ 'data-testid': 'zip-code' }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              source="country"
              label="Country"
              fullWidth
              inputProps={{ 'data-testid': 'country' }}
            />
          </Grid>
        </Grid>
        <AddressVerification
          open={modalVisible}
          onClose={toggleModal}
          providedAddress={providedAddress}
          verifiedAddress={suggestedAddress}
          addressStatus={addressStatus}
          addressError={addressError}
        />
      </SimpleForm>
    </Box>
  )
}
